import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, ChevronDown, History } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, EmptyState, SearchInput, SegmentedControl } from '../../../components/ui';
import { useFullscreen } from '../../../hooks/useFullscreen';
import { fetchHealth, fetchTimeline } from '../governmentApi';
import { govDate, plural } from '../format';
import {
  LEVELS,
  LEVEL_HINT,
  TimelineLevel,
  TimelineMode,
  effectsOf,
  seatOfTenure,
  spanOf,
  tierShown,
} from '../timeline';
import type { HealthIssue, HealthKind, HealthView, SeatView, TimelineView } from '../types';
import { TimelineActs } from './timeline/TimelineActs';
import { TimelineChart, TimelineSelection } from './timeline/TimelineChart';
import { TimelineDetail } from './timeline/TimelineDetail';
import { TimelineSeats } from './timeline/TimelineSeats';
import { FullscreenButton } from './FullscreenButton';

const MODES: Array<{ value: TimelineMode; label: string }> = [
  { value: 'chart', label: 'Chart' },
  { value: 'acts', label: 'Acts' },
  { value: 'seats', label: 'Posts' },
];

const CHECK_LABEL: Record<HealthKind, string> = {
  seat_contested: 'Two people in one seat',
  two_ministries: 'Minister of two ministries',
  misfiled_minister: 'Minister filed at the wrong body',
  start_contradicted: 'Start date the acts contradict',
  duplicate_tenure: 'One tenure recorded twice',
  ended_body: 'In post at a body that has ended',
  start_is_record_date: 'Start date is the day it was recorded',
};

/** How many of each kind of finding show before "Show all" */
const CHECKS_SHOWN = 4;

/** A reader's view and depth, remembered on this device */
const remembered = <T extends string>(key: string, fallback: T, allowed: readonly T[]): T => {
  try {
    const value = localStorage.getItem(key) as T | null;
    return value && allowed.includes(value) ? value : fallback;
  } catch {
    return fallback;
  }
};
const remember = (key: string, value: string) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Nothing to do: the choice lasts as long as the page
  }
};

/** Seats for a server that does not send them yet: one per post and body */
const seatsFallback = (view: TimelineView): SeatView[] => {
  const byKey = new Map<string, SeatView>();
  view.tenures.forEach((tenure, index) => {
    const key = `${tenure.post}|${(tenure.body ?? tenure.title).toLowerCase()}`;
    const tier: SeatView['tier'] =
      tenure.post === 'president' || tenure.post === 'prime_minister'
        ? 1
        : tenure.post === 'minister' || tenure.post === 'deputy_minister'
          ? 2
          : tenure.post === 'secretary_general'
            ? 3
            : 4;
    const seat = byKey.get(key) ?? { key, post: tenure.post, label: tenure.body ?? tenure.title, tier, tenures: [] };
    seat.tenures.push(index);
    byKey.set(key, seat);
  });
  return Array.from(byKey.values()).sort((a, b) => a.tier - b.tier || a.label.localeCompare(b.label));
};

/**
 * The government's history as its acts write it — who held each post, from
 * which act to which — to read three ways: on a chart over time, as the
 * chronicle of the acts, or post by post.
 */
export const TimelinePanel: React.FC<{
  /** Changes when a run finishes, so the timeline is read again */
  refreshKey?: string | null;
}> = ({ refreshKey }) => {
  const [view, setView] = useState<TimelineView | null>(null);
  const [health, setHealth] = useState<HealthView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mode, setModeState] = useState<TimelineMode>(() =>
    remembered('timeline.mode', 'chart', ['chart', 'acts', 'seats'] as const),
  );
  const [level, setLevelState] = useState<TimelineLevel>(() =>
    remembered('timeline.level', '3', ['1', '2', '3', 'all'] as const),
  );
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<TimelineSelection>(null);
  const screen = useFullscreen();

  const setMode = (next: TimelineMode) => {
    setModeState(next);
    remember('timeline.mode', next);
  };
  const setLevel = (next: TimelineLevel) => {
    setLevelState(next);
    remember('timeline.level', next);
  };

  useEffect(() => {
    let cancelled = false;
    fetchTimeline()
      .then((next) => {
        if (!cancelled) {
          setView(next);
          setError(null);
        }
      })
      .catch((reason: any) => {
        if (!cancelled) setError(reason?.message ?? 'The timeline could not be loaded.');
      });
    fetchHealth()
      .then((next) => {
        if (!cancelled) setHealth(next);
      })
      .catch(() => {
        // The checks are a help, not the page: without them the timeline still reads
      });
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  const seats = useMemo(() => (view ? (view.seats ?? seatsFallback(view)) : []), [view]);
  const shownSeats = useMemo(() => seats.filter((seat) => tierShown(seat.tier, level)), [seats, level]);
  const effects = useMemo(() => (view ? effectsOf(view) : new Map()), [view]);
  const seatOf = useMemo(
    () => (view ? seatOfTenure({ ...view, seats }) : new Map<number, SeatView>()),
    [view, seats],
  );
  const span = useMemo(() => (view ? spanOf(view) : { from: 0, to: 1 }), [view]);

  const checks = health?.issues.length ? <Checks issues={health.issues} /> : null;

  if (error) {
    return <p className="rounded-xl bg-primary-50 px-3.5 py-2.5 text-[13px] text-primary-700">{error}</p>;
  }
  if (!view) return <div className="h-40 animate-pulse rounded-2xl bg-neutral-100" />;
  if (!view.acts.length) {
    return (
      <div className="space-y-4">
        {checks}
        <Card>
          <EmptyState
            icon={History}
            title="No acts on file yet"
            action={
              <p className="max-w-sm text-[12px] leading-snug text-neutral-400">
                Research → “Who held each post” reads the decrees that formed the government and
                dates every holder from the act that first appointed them.
              </p>
            }
          />
        </Card>
      </div>
    );
  }

  const read = view.acts.filter((act) => act.status === 'read').length;
  const toRead = view.acts.filter((act) => act.status === 'found').length;

  /** Choosing something takes the reader to where it reads best */
  const choose = (next: TimelineSelection) => {
    setSelected(next);
    if (next?.kind === 'tenure' && mode !== 'chart') setMode('chart');
  };
  const chooseAct = (id: string) => {
    setSelected({ kind: 'act', id });
    if (mode === 'seats') setMode('acts');
  };

  const full = screen.active;

  return (
    <div className="space-y-4">
      {checks}

      {/* Over the whole screen the tools stay on top and the view takes the rest */}
      <div
        className={
          full
            ? 'fixed inset-0 z-[45] flex flex-col gap-3 bg-[#f7f7f7] px-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-[max(0.75rem,env(safe-area-inset-top))] sm:px-5'
            : 'space-y-4'
        }
      >
        {/* ---- what to show ---- */}
        {/* On a phone the search and full screen share the first line */}
        <div className="flex flex-wrap items-center gap-2">
          <SearchInput
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Find a person, a post or an act"
            className="order-1 min-w-0 flex-1 sm:w-72 sm:flex-none"
          />
          <FullscreenButton active={full} onToggle={screen.toggle} className="order-2 sm:order-3" />
          <div className="order-3 -mx-4 flex max-w-[calc(100%+2rem)] items-center gap-2 overflow-x-auto px-4 sm:order-2 sm:mx-0 sm:max-w-full sm:px-0">
            <SegmentedControl
              label="View"
              className="flex-shrink-0"
              items={MODES}
              value={mode}
              onChange={(value) => setMode(value as TimelineMode)}
            />
            <span title={LEVEL_HINT[level]} className="flex-shrink-0">
              <SegmentedControl
                label="Depth"
                items={LEVELS}
                value={level}
                onChange={(value) => setLevel(value as TimelineLevel)}
              />
            </span>
          </div>
          <p className="order-4 w-full text-[12px] text-neutral-400 lg:ml-auto lg:w-auto">
            {plural(view.acts.length, 'act')}, {read} read
            {view.latestComplete ? ` · last full government ${govDate(view.latestComplete.date)}` : ''}
          </p>
        </div>
        {toRead > 0 && (
          <p className="rounded-xl bg-amber-50 px-3.5 py-2.5 text-[12.5px] text-amber-800">
            {plural(toRead, 'act')} still to read — the timeline is incomplete until they are. Run it again from
            Research.
          </p>
        )}

        <div
          className={
            !full
              ? 'space-y-4'
              : mode === 'chart'
                ? 'relative flex min-h-0 flex-1 gap-3'
                : 'min-h-0 flex-1 overflow-y-auto overscroll-contain'
          }
        >
          {mode === 'chart' && (
            <>
              <div className={full ? 'flex min-h-0 min-w-0 flex-1 flex-col' : ''}>
                <TimelineChart
                  view={view}
                  seats={shownSeats}
                  query={query}
                  selected={selected}
                  onSelect={setSelected}
                  expanded={full}
                />
              </div>
              {selected && (
                // Over the whole screen: a column of its own on a wide one,
                // a card over the chart's foot on a narrow one
                <div
                  className={
                    full
                      ? 'absolute inset-x-0 bottom-0 z-[41] max-h-[60%] overflow-y-auto overscroll-contain rounded-2xl shadow-[0_24px_60px_-28px_rgba(0,0,0,0.45)] lg:static lg:max-h-none lg:w-[380px] lg:flex-shrink-0 lg:shadow-none'
                      : ''
                  }
                >
                  <TimelineDetail
                    selected={selected}
                    acts={view.acts}
                    tenures={view.tenures}
                    effects={effects}
                    seatOf={seatOf}
                    level={level}
                    onSelect={setSelected}
                    column={full}
                  />
                </div>
              )}
            </>
          )}
          {mode === 'acts' && (
            <TimelineActs
              acts={view.acts}
              tenures={view.tenures}
              effects={effects}
              seatOf={seatOf}
              level={level}
              query={query}
              selectedAct={selected?.kind === 'act' ? selected.id : null}
              onSelectTenure={(index) => choose({ kind: 'tenure', index })}
            />
          )}
          {mode === 'seats' && (
            <TimelineSeats
              seats={shownSeats}
              tenures={view.tenures}
              span={span}
              query={query}
              selectedTenure={selected?.kind === 'tenure' ? selected.index : null}
              onSelectTenure={(index) => choose({ kind: 'tenure', index })}
              onSelectAct={chooseAct}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const Collapsible: React.FC<{
  title: string;
  detail?: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}> = ({ title, detail, defaultOpen = false, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card className="overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 px-4 py-3 text-left transition-colors hover:bg-neutral-50"
      >
        <span className="min-w-0 flex-1 text-[13px] text-ink">{title}</span>
        {detail && <span className="text-[12px] text-neutral-400">{detail}</span>}
        <ChevronDown
          className={`h-4 w-4 flex-shrink-0 text-neutral-400 transition-transform ${open ? 'rotate-180' : ''}`}
          strokeWidth={1.75}
        />
      </button>
      {open && <div className="border-t border-neutral-200/70">{children}</div>}
    </Card>
  );
};

/** What is wrong with the directory's government, most serious first */
const Checks: React.FC<{ issues: HealthIssue[] }> = ({ issues }) => {
  const groups = useMemo(() => {
    const byKind = new Map<HealthKind, HealthIssue[]>();
    for (const issue of issues) byKind.set(issue.kind, [...(byKind.get(issue.kind) ?? []), issue]);
    return Array.from(byKind.entries());
  }, [issues]);
  const errors = issues.filter((issue) => issue.severity === 'error').length;
  const [all, setAll] = useState<Set<HealthKind>>(new Set());
  return (
    <Collapsible
      title="Checks"
      detail={`${errors ? `${errors} wrong for certain · ` : ''}${plural(issues.length - errors, 'thing')} to look at`}
    >
      <div className="divide-y divide-neutral-100">
        {groups.map(([kind, list]) => (
          <div key={kind} className="px-4 py-3">
            <p className="flex items-center gap-2 text-[12.5px] text-ink">
              <AlertTriangle
                className={`h-3.5 w-3.5 flex-shrink-0 ${list[0].severity === 'error' ? 'text-primary-600' : 'text-amber-500'}`}
                strokeWidth={1.75}
              />
              {CHECK_LABEL[kind]}
              <span className="text-neutral-400">{list.length}</span>
            </p>
            <ul className="mt-1.5 space-y-1.5">
              {(all.has(kind) ? list : list.slice(0, CHECKS_SHOWN)).map((issue, index) => (
                <li key={`${kind}-${index}`} className="break-words pl-5 text-[12px] leading-snug text-neutral-600">
                  {issue.summary}
                  {issue.stakeholderIds?.[0] && (
                    <>
                      {' '}
                      <Link
                        to={`/stakeholders/${issue.stakeholderIds[0]}`}
                        className="text-neutral-400 underline decoration-neutral-300 underline-offset-2 hover:text-ink"
                      >
                        Open
                      </Link>
                    </>
                  )}
                </li>
              ))}
            </ul>
            {list.length > CHECKS_SHOWN && !all.has(kind) && (
              <button
                type="button"
                onClick={() => setAll((current) => new Set(current).add(kind))}
                className="mt-1.5 pl-5 text-[12px] text-neutral-500 underline decoration-neutral-300 underline-offset-2 hover:text-ink"
              >
                Show all {list.length}
              </button>
            )}
          </div>
        ))}
      </div>
    </Collapsible>
  );
};
